import FileDownload from "@mui/icons-material/FileDownload";
import { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useScheduleCacheEnabled } from "../../../hooks/useScheduleWeekRules";
import { useScheduleWeeks } from "../../../hooks/useScheduleWeeks";
import { useShownWeek } from "../../../hooks/useShownWeek";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { ScheduleExportPopup } from "./ScheduleExportPopup";
import { exportableWeeks } from "./weekRange";

export type ScheduleExportButtonProps = {
	/** The week currently on screen; seeds the weekly tab's default range. */
	weekIndex: number;
};

/** The single export trigger for the staffing grid, shared by the v1 and v2 headers. */
export const ScheduleExportButton: FC<ScheduleExportButtonProps> = ({ weekIndex }) => {
	const { t } = useTranslation();
	const { hospitalData } = useHospitalData();
	const isCacheEnabled = useScheduleCacheEnabled();
	const { weekStart } = useShownWeek();
	// DEFAULT: today's week until the page names one.
	const { firstDate } = useScheduleWeeks(weekStart ?? DateTime.now());
	const [isOpen, setIsOpen] = useState(false);

	// Counted the same way the tabs count, not off raw `weeks`: a week carrying no shift rows has no
	// date to export by, so counting it here opened a popup whose download was already disabled.
	// With the cache on, any week of the schedule exports, so there are weeks once the schedule's first day is known.
	const hasWeeks = isCacheEnabled ? firstDate !== undefined : exportableWeeks(hospitalData).length > 0;

	return (
		<>
			<SystemButton
				data-testid="staffing-export-button"
				data-annie-target={hasWeeks ? "staffing-export-button" : undefined}
				buttonVariant="borderless"
				onClick={() => setIsOpen(true)}
				disabled={!hasWeeks}
				leadingIconGap={0}
				leadingIcon={<FileDownload sx={{ color: "primary.main" }} />}
				sx={{ bgcolor: "transparent" }}
				tooltip={hasWeeks ? t("export") : t("download.no.weeks.available")}
				aria-label={t("export")}
			/>
			{isOpen && <ScheduleExportPopup open={isOpen} onClose={() => setIsOpen(false)} weekIndex={weekIndex} />}
		</>
	);
};
